/**
 * The glyph and the tint one MELS activity category is drawn with.
 *
 * Split out of the 23 September crystal squares when the client moved the card onto the
 * boundaries (`ActivityHover.tsx`): the mapping is a property of the category, not of the
 * mark that happened to carry it, and it is now read from two places — the hover card's rows
 * and the tooltip builder in `activityTips.ts`.
 */
import type { ActivityPathway } from '../../lib/api';
import { ICONS, type IconName } from '../../lib/icons';

/**
 * Pathway → tint. The three programme pathways are the portal's own three accent hues
 * (`--brand`, `--plum`, `--blue` in tokens.css), written out here because this markup is a
 * string assigned to `innerHTML` and a `var()` in an inline style would resolve against the
 * Leaflet pane rather than against anything this file controls.
 *
 * `uncategorised` never appears in a count — the server drops those activities, which is the
 * client's instruction — but the dictionary can carry the pathway, so it has a colour rather
 * than a fallback that would silently look like agroecology.
 */
export const PATHWAY_TINT: Record<ActivityPathway, string> = {
  agroecology: '#2E7D32',
  markets_finance: '#8E3B62',
  enabling_environment: '#2B6CB0',
  uncategorised: '#8B958A',
};

/** The tint for a pathway the union does not carry — a thirteenth family, a renamed one. */
export function tintFor(pathway: ActivityPathway): string {
  return PATHWAY_TINT[pathway] ?? PATHWAY_TINT.uncategorised;
}

/**
 * MELS category code → a glyph from the portal's §F2 icon set.
 *
 * Keyed by the **code**, not by the label: the label is translated and the id is a database
 * key, and the code is the one stable, human-readable handle MELS publishes for a category.
 * An unknown code (a thirteenth category, a renamed one) falls back to `tag` rather than to
 * nothing — a row with no glyph would break the card's alignment, and a wrong glyph is a
 * smaller lie than a hole.
 */
export const CATEGORY_ICON: Record<string, IconName> = {
  CBT: 'sprout',
  DP: 'play',
  ISS: 'wrench',
  TWM: 'droplet',
  MFF: 'chart',
  CME: 'globe',
  DCM: 'table',
  IOS: 'building',
  PDA: 'shield',
  KL: 'legend',
  COM: 'mail',
  GESI: 'users',
};

export const FALLBACK_ICON: IconName = 'tag';

export function iconFor(code: string): IconName {
  return CATEGORY_ICON[code] ?? FALLBACK_ICON;
}

/** `<Icon/>` as a string — same viewBox, stroke and caps, for markup Leaflet owns. */
export function iconSvg(name: IconName, size = 13): string {
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor"` +
    ` stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
    `<path d="${ICONS[name]}"/></svg>`
  );
}
